/**
 * The report explorer: group leads by one or two dimensions and total the
 * money, with the configuration savable.
 *
 * The dimension list comes from `GET /api/v1/meta/enums`. `explore_dim_sql` in
 * the server returns `Err("Unknown group_by: …")` for anything outside its
 * set, so a hard-coded list here would eventually offer something that returns
 * a 400 instead of a chart — the same capability drift the served enums exist
 * to prevent.
 */
import { useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, Group, Loader, Menu, Modal, Select,
  SimpleGrid, Stack, Table, Text, TextInput, Tooltip,
} from '@mantine/core';
import { IconBookmark, IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import {
  useDeleteReportView, useEnums, useExplore, useReportViews, useSaveReportView,
  type ExploreBody,
} from '../api/queries';
import { TOKENS, formatGBP, formatNumber } from '../theme';
import { SpeedChip } from '../ui';
import { useVocab, type Vocab } from '../vocab';

function dimensionLabel(v: Vocab, value: string): string | undefined {
  switch (value) {
    case 'campaign': return v.One('campaign');
    case 'buyer': return v.One('buyer');
    case 'supplier': return v.One('supplier');
    case 'source': return v.p.sourceTag;
    default: return undefined;
  }
}

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

function Total({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
      <Text fz={22} fw={700}>{value}</Text>
    </Card>
  );
}

export function ReportsExplore() {
  const enums = useEnums();
  const views = useReportViews();
  const saveView = useSaveReportView();
  const deleteView = useDeleteReportView();

  const [primary, setPrimary] = useState<string | null>('campaign');
  const [secondary, setSecondary] = useState<string | null>(null);
  const [range, setRange] = useState<string>('30d');
  const [saving, setSaving] = useState(false);
  const [viewName, setViewName] = useState('');

  const v = useVocab();
  // The served labels are today's words; the values are the contract.
  const dimensions = (enums.data?.explore_dimensions ?? []).map((d) => ({
    ...d,
    label: dimensionLabel(v, d.value) ?? d.label,
  }));
  const body: ExploreBody = {
    group_by: [primary, secondary].filter((d): d is string => !!d),
    date_range: range,
  };
  const { data, isPending, error } = useExplore(body, primary !== null);
  // Which money this login may see; the server has already blanked the rest.
  const money = data?.money ?? { revenue: true, cost: false, margin: false };
  const ownCost = money.cost && !money.revenue;

  function applyView(config: unknown) {
    const c = (config ?? {}) as { group_by?: string[]; date_range?: string };
    setPrimary(c.group_by?.[0] ?? 'campaign');
    setSecondary(c.group_by?.[1] ?? null);
    setRange(c.date_range ?? '30d');
  }

  return (
    <Stack>
      <Card>
        <Group align="flex-end" gap="sm" wrap="wrap">
          <Select
            label="Group by"
            data={dimensions.map((d) => ({ value: d.value, label: d.label }))}
            value={primary}
            onChange={setPrimary}
            allowDeselect={false}
            w={180}
          />
          <Select
            label="Then by"
            placeholder="Nothing"
            clearable
            // A dimension cannot pivot against itself; the server would group
            // by the same expression twice and return one row per bucket.
            data={dimensions
              .filter((d) => d.value !== primary)
              .map((d) => ({ value: d.value, label: d.label }))}
            value={secondary}
            onChange={setSecondary}
            w={180}
          />
          <Select
            label="Period"
            data={DATE_PRESETS}
            value={range}
            onChange={(v) => setRange(v ?? '30d')}
            allowDeselect={false}
            w={160}
          />

          <Group gap="xs" ml="auto">
            {views.data && views.data.views.length > 0 && (
              <Menu position="bottom-end">
                <Menu.Target>
                  <Button variant="default" leftSection={<IconBookmark size={16} />}>
                    Saved views
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {views.data.views.map((v) => (
                    <Menu.Item
                      key={v.id}
                      onClick={() => applyView(v.config)}
                      rightSection={
                        <ActionIcon
                          component="div"
                          variant="subtle"
                          color="red"
                          size="xs"
                          aria-label={`Delete ${v.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteView.mutate(v.id);
                          }}
                        >
                          <IconTrash size={13} />
                        </ActionIcon>
                      }
                    >
                      {v.name}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}
            <Tooltip label="Save this grouping and period" withArrow>
              <Button
                variant="default"
                leftSection={<IconDeviceFloppy size={16} />}
                onClick={() => { setViewName(''); setSaving(true); }}
              >
                Save
              </Button>
            </Tooltip>
          </Group>
        </Group>
      </Card>

      {error && <Alert color="red" variant="light">{error.message}</Alert>}

      {data && (
        <SimpleGrid cols={{ base: 2, sm: 3 + (money.revenue ? 1 : 0) + (money.cost ? 1 : 0) + (money.margin ? 1 : 0) }}>
          <Total label={v.Many('lead')} value={formatNumber(data.totals.leads)} />
          <Total label={v.p.Sold} value={formatNumber(data.totals.sold)} />
          {money.revenue && <Total label="Revenue" value={formatGBP(data.totals.revenue)} />}
          {money.cost && <Total label={ownCost ? 'Earned' : 'Cost'} value={formatGBP(data.totals.payout)} />}
          {money.margin && (
            <Total
              label="Margin"
              value={`${formatGBP(data.totals.profit)}${data.totals.margin_pct != null ? ` (${data.totals.margin_pct.toFixed(1)}%)` : ''}`}
            />
          )}
          <Total label="Conversion" value={`${data.totals.conversion_rate.toFixed(1)}%`} />
        </SimpleGrid>
      )}

      <Card p={0}>
        {isPending ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : (
          <Table.ScrollContainer minWidth={820}>
            <Table striped horizontalSpacing={6} verticalSpacing={7}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{dimensions.find((d) => d.value === primary)?.label ?? 'Group'}</Table.Th>
                  {data?.is_pivot && (
                    <Table.Th>
                      {dimensions.find((d) => d.value === secondary)?.label ?? 'Then by'}
                    </Table.Th>
                  )}
                  <Table.Th ta="right">{v.Many('lead')}</Table.Th>
                  <Table.Th ta="right">{v.p.Sold}</Table.Th>
                  <Table.Th ta="right">Conversion</Table.Th>
                  {money.revenue && <Table.Th ta="right">Revenue</Table.Th>}
                  {money.cost && <Table.Th ta="right">{ownCost ? 'Earned' : 'Cost'}</Table.Th>}
                  {money.margin && <Table.Th ta="right">Margin</Table.Th>}
                  {money.margin && <Table.Th ta="right">Margin %</Table.Th>}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data?.rows.length === 0 && (
                  <Table.Tr><Table.Td colSpan={9}>
                    <Text ta="center" c="dimmed" py="lg">Nothing in this period.</Text>
                  </Table.Td></Table.Tr>
                )}
                {data?.rows.map((r, i) => (
                  <Table.Tr key={`${r.key}-${r.key2 ?? ''}-${i}`}>
                    <Table.Td><Text size="sm" truncate>{r.label || '(none)'}</Text></Table.Td>
                    {data.is_pivot && (
                      <Table.Td><Text size="sm" truncate>{r.label2 || '(none)'}</Text></Table.Td>
                    )}
                    <Table.Td ta="right">{formatNumber(r.leads)}</Table.Td>
                    <Table.Td ta="right">{formatNumber(r.sold)}</Table.Td>
                    <Table.Td ta="right">
                      <Badge
                        size="sm"
                        variant="light"
                        color={r.conversion_rate >= 50 ? 'chartGreen' : r.conversion_rate > 0 ? 'gray' : 'chartRed'}
                      >
                        {r.conversion_rate.toFixed(1)}%
                      </Badge>
                    </Table.Td>
                    {money.revenue && (
                      <Table.Td ta="right"><Text size="sm" fw={600}>{formatGBP(r.revenue)}</Text></Table.Td>
                    )}
                    {money.cost && <Table.Td ta="right">{formatGBP(r.payout)}</Table.Td>}
                    {money.margin && (
                      <Table.Td ta="right">
                        <Text size="sm" c={r.profit < 0 ? TOKENS.redText : undefined}>{formatGBP(r.profit)}</Text>
                      </Table.Td>
                    )}
                    {money.margin && (
                      <Table.Td ta="right">{r.margin_pct != null ? `${r.margin_pct.toFixed(1)}%` : '—'}</Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>
      {data?.query_stats && (
        <Group justify="flex-end" mt={-6}>
          <SpeedChip stats={data.query_stats} />
        </Group>
      )}

      <Modal opened={saving} onClose={() => setSaving(false)} title="Save this view" size="sm">
        <Stack>
          <TextInput
            data-autofocus
            label="Name"
            placeholder={`e.g. ${v.Many('buyer')} by week`}
            value={viewName}
            onChange={(e) => setViewName(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && viewName.trim()) {
                saveView.mutate(
                  { name: viewName.trim(), config: body },
                  { onSuccess: () => setSaving(false) },
                );
              }
            }}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setSaving(false)}>Cancel</Button>
            <Button
              loading={saveView.isPending}
              disabled={!viewName.trim()}
              onClick={() =>
                saveView.mutate(
                  { name: viewName.trim(), config: body },
                  { onSuccess: () => setSaving(false) },
                )
              }
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
